import { useEffect, useState } from "react";
import type { DashboardRefresh, DashboardTimeRange } from "@/lib/dashboard/types";
import { scheduledBoundaries } from "@/lib/dashboard/scheduledTimeRange";
import { calendarWeekStart } from "@/lib/dashboard/timeRange";
import { getRefreshSchedule } from "@/lib/dashboard/timePresets";

export const AUTO_REFRESH_INTERVAL_MS = 60_000;
export const LIVE_QUERY_STALE_TIME_MS = AUTO_REFRESH_INTERVAL_MS;

// Report dashboards (e.g. the war room) are refreshed once a day, not
// near-real-time: SOC analysts do not sit watching them, so there is no
// reason to poll the backend on the live cadence.
export const REPORT_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;
export const REPORT_QUERY_STALE_TIME_MS = REPORT_REFRESH_INTERVAL_MS;

export function getRefreshTime(now = Date.now()): number {
  return Math.floor(now / AUTO_REFRESH_INTERVAL_MS) * AUTO_REFRESH_INTERVAL_MS;
}

export function getReportRefreshTime(now = Date.now()): number {
  return Math.floor(now / REPORT_REFRESH_INTERVAL_MS) * REPORT_REFRESH_INTERVAL_MS;
}

interface VisibilityDocument {
  visibilityState: DocumentVisibilityState;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

interface RefreshSchedulerEnvironment {
  document: VisibilityDocument;
  setTimeout(callback: () => void, delay: number): ReturnType<typeof setTimeout>;
  clearTimeout(timeoutId: ReturnType<typeof setTimeout>): void;
}

export function createAutoRefreshScheduler(
  refresh: () => void,
  environment: RefreshSchedulerEnvironment,
): () => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  function schedule() {
    timeoutId = environment.setTimeout(() => {
      if (environment.document.visibilityState === "visible") refresh();
      schedule();
    }, AUTO_REFRESH_INTERVAL_MS);
  }

  function handleVisibilityChange() {
    if (environment.document.visibilityState !== "visible") return;
    if (timeoutId !== undefined) environment.clearTimeout(timeoutId);
    refresh();
    schedule();
  }

  schedule();
  environment.document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    if (timeoutId !== undefined) environment.clearTimeout(timeoutId);
    environment.document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}

/**
 * Supplies a shared timestamp for analytics queries and advances it on the
 * refresh cadence or immediately when a background tab becomes visible.
 */
export function useAutoRefresh(): number {
  const [referenceTime, setReferenceTime] = useState(() => getRefreshTime());

  useEffect(() => {
    return createAutoRefreshScheduler(() => setReferenceTime(getRefreshTime()), {
      document,
      setTimeout: (callback, delay) => globalThis.setTimeout(callback, delay),
      clearTimeout: (timeoutId) => globalThis.clearTimeout(timeoutId),
    });
  }, []);

  return referenceTime;
}

/**
 * Same idea as `createAutoRefreshScheduler`, on the slow report cadence and
 * without its immediate-refresh-on-focus behavior: a report dashboard is
 * meant to spare the backend, so a user merely switching back to the tab is
 * not the "interaction" that should force a refetch. A fresh mount still
 * picks up the current tick, and react-query's own staleTime governs actual
 * interaction-driven refetches (e.g. a manual retry).
 */
export function createReportRefreshScheduler(
  refresh: () => void,
  environment: RefreshSchedulerEnvironment,
): () => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  function schedule() {
    timeoutId = environment.setTimeout(() => {
      if (environment.document.visibilityState === "visible") refresh();
      schedule();
    }, REPORT_REFRESH_INTERVAL_MS);
  }

  schedule();

  return () => {
    if (timeoutId !== undefined) environment.clearTimeout(timeoutId);
  };
}

/**
 * Supplies a shared timestamp for report dashboards (rarely refreshed, e.g.
 * once a day for the war room). See `createReportRefreshScheduler` for why it
 * does not react to tab visibility the way `useAutoRefresh` does.
 */
export function useReportRefresh(): number {
  const [referenceTime, setReferenceTime] = useState(() => getReportRefreshTime());

  useEffect(() => {
    return createReportRefreshScheduler(() => setReferenceTime(getReportRefreshTime()), {
      document,
      setTimeout: (callback, delay) => globalThis.setTimeout(callback, delay),
      clearTimeout: (timeoutId) => globalThis.clearTimeout(timeoutId),
    });
  }, []);

  return referenceTime;
}

export function createDashboardRefreshScheduler(
  refresh: () => void,
  config: DashboardRefresh,
  environment: RefreshSchedulerEnvironment,
  now: () => number = Date.now,
  timeRange?: DashboardTimeRange,
): () => void {
  const intervalMs = config.intervalSeconds * 1000;
  const dailyRefresh = getRefreshSchedule(config) === "daily-utc";
  const [hours, minutes] = (config.time ?? "00:00").split(":").map(Number);
  const dailyOffsetMs = (hours * 60 + minutes) * 60_000;
  function previousDailyRefresh() {
    return (
      Math.floor((now() - dailyOffsetMs) / REPORT_REFRESH_INTERVAL_MS) *
        REPORT_REFRESH_INTERVAL_MS +
      dailyOffsetMs
    );
  }
  let lastRefresh = now();
  let timeoutId: ReturnType<typeof setTimeout>;
  function update() {
    lastRefresh = now();
    refresh();
  }
  function boundaries() {
    if (timeRange?.mode === "scheduled") return scheduledBoundaries(timeRange.schedule, now());
    if (timeRange?.mode === "relative" && timeRange.unit === "calendar-week") {
      const previous = calendarWeekStart(now());
      return { previous, next: previous + 604_800_000 };
    }
    return undefined;
  }
  function windowAdvanced() {
    return (boundaries()?.previous ?? -Infinity) > lastRefresh;
  }
  function refreshDue() {
    return dailyRefresh ? previousDailyRefresh() > lastRefresh : now() - lastRefresh >= intervalMs;
  }
  function schedule() {
    const boundary = boundaries()?.next;
    timeoutId = environment.setTimeout(
      () => {
        if (
          environment.document.visibilityState === "visible" &&
          (refreshDue() || windowAdvanced())
        )
          update();
        schedule();
      },
      Math.min(
        dailyRefresh
          ? previousDailyRefresh() + REPORT_REFRESH_INTERVAL_MS - now()
          : Math.max(intervalMs - (now() - lastRefresh), 60_000),
        boundary === undefined ? Infinity : Math.max(boundary - now(), 1),
        2_147_483_647,
      ),
    );
  }
  function onVisibility() {
    if (environment.document.visibilityState !== "visible") return;
    if (
      windowAdvanced() ||
      (dailyRefresh && refreshDue()) ||
      config.onFocus === "always" ||
      (config.onFocus === "if-stale" && refreshDue())
    ) {
      environment.clearTimeout(timeoutId);
      update();
      schedule();
    }
  }
  schedule();
  environment.document.addEventListener("visibilitychange", onVisibility);
  return () => {
    environment.clearTimeout(timeoutId);
    environment.document.removeEventListener("visibilitychange", onVisibility);
  };
}

export function useDashboardRefresh(
  config: DashboardRefresh,
  enabled = true,
  timeRange?: DashboardTimeRange,
): number {
  const [referenceTime, setReferenceTime] = useState(getRefreshTime);
  const boundaryPolicy =
    timeRange?.mode === "scheduled" ||
    (timeRange?.mode === "relative" && timeRange.unit === "calendar-week")
      ? JSON.stringify(timeRange)
      : undefined;
  useEffect(() => {
    if (!enabled) return;
    const policy = boundaryPolicy ? (JSON.parse(boundaryPolicy) as DashboardTimeRange) : undefined;
    setReferenceTime(getRefreshTime());
    return createDashboardRefreshScheduler(
      () => setReferenceTime(getRefreshTime()),
      config,
      {
        document,
        setTimeout: (callback, delay) => globalThis.setTimeout(callback, delay),
        clearTimeout: (timer) => globalThis.clearTimeout(timer),
      },
      Date.now,
      policy,
    );
  }, [
    config.intervalSeconds,
    config.onFocus,
    config.schedule,
    config.time,
    enabled,
    boundaryPolicy,
  ]);
  return referenceTime;
}
